import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function OfflineIndicator() {
  const { csrfToken } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showIndicator, setShowIndicator] = useState(!navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      setShowIndicator(true);

      // Check for pending items and sync
      const count = await checkPendingSync();
      if (count > 0 && !syncing) {
        await performSync();
      }

      setTimeout(() => setShowIndicator(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
    };

    const handleVisibilityChange = async () => {
      // When user returns to tab, check if online and sync if needed
      if (!document.hidden && navigator.onLine) {
        const count = await checkPendingSync();
        if (count > 0 && !syncing) {
          await performSync();
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial check for pending items
    checkPendingSync();

    // Periodic check every 10 seconds if we have pending items
    const intervalId = setInterval(async () => {
      const currentlyOnline = navigator.onLine;
      setIsOnline(currentlyOnline);

      const count = await checkPendingSync();

      // Auto-sync if online and have pending items
      if (currentlyOnline && count > 0 && !syncing) {
        await performSync();
      }
    }, 10000); // Check every 10 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [syncing]);

  const checkPendingSync = async () => {
    try {
      const { getPendingSyncCount } = await import('../utils/offlineDB');
      const count = await getPendingSyncCount();
      setPendingSync(count);
      return count;
    } catch (error) {
      console.error('Error checking pending sync:', error);
      return 0;
    }
  };

  const performSync = async () => {
    if (syncing || !navigator.onLine) return;

    setSyncing(true);
    try {
      const { syncOfflineData } = await import('../utils/syncOfflineData');
      const result = await syncOfflineData(csrfToken);

      console.log('Sync result:', result);

      if (result.synced > 0) {
        // Show success message
        setTimeout(() => {
        }, 500);
      }

      // Recheck pending count
      await checkPendingSync();
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  if (!showIndicator && isOnline && pendingSync === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        padding: '0.75rem 1.25rem',
        borderRadius: '12px',
        background: isOnline
          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
          : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: 'white',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '14px',
        fontWeight: '600',
        animation: 'slideDown 0.3s ease-out'
      }}
    >
      {isOnline ? (
        <>
          <Wifi size={20} />
          <span>Online</span>
          {(pendingSync > 0 || syncing) && (
            <>
              <span style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                fontSize: '12px'
              }}>
                {syncing ? `Synkronoidaan ${pendingSync} kohdetta...` : `${pendingSync} odottaa synkronointia`}
              </span>
              {!syncing && pendingSync > 0 && (
                <button
                  onClick={performSync}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.75rem',
                    background: 'rgba(255, 255, 255, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Synkronoi nyt
                </button>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <WifiOff size={20} />
          <span>Offline-tila</span>
          {pendingSync > 0 && (
            <>
              <AlertCircle size={16} style={{ marginLeft: '0.25rem' }} />
              <span style={{
                marginLeft: '0.25rem',
                padding: '0.25rem 0.5rem',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                fontSize: '12px'
              }}>
                {pendingSync} odottaa lähetystä
              </span>
            </>
          )}
        </>
      )}

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
